"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, ShieldCheck, ThermometerSnowflake, ArrowRight, Gauge, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  id: "1",
  title: "Controlo térmico preciso",
  description: "Modelação e injeção de precisão para estabilização de gases a altas temperaturas."
}, {
  id: "2",
  title: "Atomização fina e uniforme",
  description: "Bicos injetores que evitam acumulação de resíduos e protegem os filtros de mangas."
}, {
  id: "3",
  title: "Eficiência energética e ambiental",
  description: "Redução do consumo de reagentes químicos e conformidade rigorosa com normas de emissão."
}];
const technicalSpecs = [{
  id: "spec-1",
  label: "Eficiência de Arrefecimento",
  value: "> 99.4%"
}, {
  id: "spec-2",
  label: "Gama de Temperatura",
  value: "Até 1200 °C"
}, {
  id: "spec-3",
  label: "Tempo de Evaporação",
  value: "< 1.2 s"
}];
export default function GasConditioning() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="herogasconditioning" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-20 md:py-28 lg:py-32">
      {/* Subtle ambient gradient overlay and grid texture */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top_right,hsl(215_90%_45%/0.25),transparent_65%)]" />
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-[radial-gradient(hsl(210_20%_98%)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Copy Content */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start gap-6">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                Engenharia de Processos
              </Badge>
              <span className="text-xs text-primary-foreground/75 font-mono">
                Spraying Systems Co. desde 1993
              </span>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-primary-foreground leading-[1.12]">
              Condicionamento & Arrefecimento de Gases
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-primary-foreground/85 leading-relaxed max-w-2xl font-normal">Otimize o controlo de temperatura em condutas, fornos e torres de arrefecimento. Sistemas de pulverização de alta tecnologia para proteger equipamentos e garantir a despoluição de efluentes gasosos. </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md font-semibold px-7">
                <Link to="/ContactsPage">
                  Consulte-nos
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground bg-primary/40 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CatalogsPage">
                  <FileText className="size-4 mr-2" />
                  Ver Catálogos
                </Link>
              </Button>
            </div>

            {/* Micro Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-primary-foreground/15 w-full mt-2">
              {technicalSpecs.map((spec, i) => <div key={spec.id} data-index={i} className="flex flex-col gap-1">
                  <span className="font-mono text-xl sm:text-2xl font-bold text-primary-foreground">
                    {spec.value}
                  </span>
                  <span className="text-xs text-primary-foreground/75 font-sans">
                    {spec.label}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Technical Visual & Card Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.15
        }} className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-2xl bg-primary-foreground/10 border border-primary-foreground/15 p-6 backdrop-blur-sm shadow-xl flex flex-col gap-6">
              
              <div className="flex items-center justify-between border-b border-primary-foreground/10 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground shadow-xs">
                    <ThermometerSnowflake className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-default text-base font-semibold text-primary-foreground">
                      Soluções de Arrefecimento
                    </h3>
                    <p className="text-xs text-primary-foreground/70">
                      Sistemas AutoJet & Lanças Customizadas
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-primary-foreground border-primary-foreground/25 bg-primary/20 text-2xs">
                  ISO 9001
                </Badge>
              </div>

              {/* Feature Points list */}
              <div className="space-y-4">
                {features.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3.5 p-3 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10 transition-[background-color,border-color] duration-200 hover:bg-primary-foreground/10">
                    <CheckCircle2 className="size-4 text-chart-3 shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <h4 className="text-sm font-semibold text-primary-foreground">
                        {item.title}
                      </h4>
                      <p className="text-xs text-primary-foreground/75 leading-normal">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              {/* Status Bar */}
              <div className="flex items-center justify-between pt-2 text-xs text-primary-foreground/80">
                <div className="flex items-center gap-2">
                  <Gauge className="size-3.5 text-secondary" />
                  <span>Modelação CFD disponível</span>
                </div>
                <div className="flex items-center gap-1.5 text-chart-3 font-medium">
                  <ShieldCheck className="size-3.5" />
                  <span>Garantia Eurocontrol</span>
                </div>
              </div>

            </div>

            {/* Bottom auxiliary link card */}
            <div className="rounded-xl bg-primary-foreground/5 border border-primary-foreground/10 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Flame className="size-4 text-chart-4" />
                <span className="text-xs sm:text-sm text-primary-foreground/90 font-medium">
                  Cálculo de caudal e arrefecimento para a sua conduta
                </span>
              </div>
              <Button asChild variant="ghost" size="sm" className="text-xs font-semibold text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/SprayLancesAndManifoldsPage">
                  Explorar
                  <ArrowRight className="size-3 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}